import { useCallback, useEffect, useState } from "react";
import { Download, Search, Send, ShieldCheck, Wallet, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { can } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { PageHead, EmptyState } from "@/components/Bits";
import { FreeEntryButton } from "@/components/StandaloneEntry";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_TINT = {
  pending_payment_confirmation: "bg-amber-100 text-amber-800",
  payment_confirmed: "bg-blue-100 text-blue-800",
  active: "bg-emerald-100 text-emerald-800",
  expired: "bg-slate-200 text-slate-700",
  cancelled: "bg-rose-100 text-rose-700",
  rejected: "bg-rose-100 text-rose-700",
};
const StatusPill = ({ s }) => (
  <Badge className={`border-0 capitalize ${STATUS_TINT[s] || "bg-slate-100"}`}>
    {String(s).replace(/_/g, " ")}
  </Badge>
);

const viewProof = async (path) => {
  try {
    const { data, headers } = await api.get(`/uploads/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
    window.open(url, "_blank");
  } catch (e) { toast.error("Could not open the proof file"); }
};

const PlansTab = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [sending, setSending] = useState("");
  const [search, setSearch] = useState("");
  const [studioFilter, setStudioFilter] = useState("all");
  const [studios, setStudios] = useState([]);
  const load = useCallback(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (studioFilter && studioFilter !== "all") params.studio_id = studioFilter;
    api.get("/protection-plans", { params }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [search, studioFilter]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    if (user?.is_network) api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
  }, [user]);
  const sendPlan = async (p) => {
    setSending(p.id);
    try {
      const { data } = await api.post(`/protection-plans/${p.id}/send`);
      if (data.email?.sent) toast.success("Protection Plan card emailed to customer");
      else toast.warning(`Not emailed — ${data.email?.reason || "no email on file"}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the plan");
    } finally {
      setSending("");
    }
  };
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="plan-list-search-input"
            className="min-h-[44px] pl-9"
            placeholder="Customer, certificate or registration no."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {user?.is_network && studios.length > 0 && (
          <Select value={studioFilter} onValueChange={setStudioFilter}>
            <SelectTrigger className="min-h-[44px] w-[200px]" data-testid="plan-studio-filter">
              <SelectValue placeholder="All studios" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <span className="text-sm text-slate-500" data-testid="plan-list-count">
          {rows.length} plans
        </span>
      </div>
      <Card className="border-brand-line shadow-none">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Certificate</TableHead><TableHead>Customer</TableHead>
              <TableHead>Studio</TableHead><TableHead>Status</TableHead>
              <TableHead>Balance (sq.ft.)</TableHead><TableHead>Valid until</TableHead>
              <TableHead>Certificate</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="p-0">
                <EmptyState icon={ShieldCheck} title="No protection plans yet"
                  hint="Plans appear here once a job owner opts in at the Delivered stage and the Accountant confirms payment."
                  testid="protection-plans-empty" />
              </TableCell></TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                <TableCell className="font-medium text-brand-ink">{p.certificate_number || "—"}</TableCell>
                <TableCell>{p.customer_name}</TableCell>
                <TableCell className="text-slate-500">{p.studio_name}</TableCell>
                <TableCell><StatusPill s={p.status} /></TableCell>
                <TableCell>{p.remaining_sqft_balance ?? p.area_sqft_covered}</TableCell>
                <TableCell className="text-slate-500">{p.valid_until || "—"}</TableCell>
                <TableCell>
                  {p.certificate_number ? (
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`plan-pdf-${p.id}`}
                        onClick={() => window.open(`${API}/protection-plans/${p.id}/certificate`, "_blank")}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {p.status === "active" && (
                        <Button size="sm" data-testid={`plan-send-${p.id}`} disabled={sending === p.id}
                          onClick={() => sendPlan(p)}>
                          <Send className="mr-1 h-3.5 w-3.5" />
                          {sending === p.id ? "Sending…" : "Send to Customer"}
                        </Button>
                      )}
                    </div>
                  ) : <span className="text-xs text-slate-400">pending</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    </div>
  );
};

const QueueTab = () => {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ amount_received: "", payment_mode: "", payment_reference: "", date_received: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/protection-plans/pending").then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (p) => {
    setActive(p);
    setForm({ amount_received: p.payment_amount ? String(p.payment_amount) : "", payment_mode: "", payment_reference: "", date_received: new Date().toISOString().slice(0, 10) });
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const payload = {
        payment_mode: form.payment_mode, payment_reference: form.payment_reference,
        date_received: form.date_received,
      };
      if (String(form.amount_received).trim() !== "") payload.amount_received = Number(form.amount_received);
      const { data } = await api.post(`/protection-plans/${active.id}/confirm-payment`, payload);
      toast.success("Payment confirmed — sent to CRM to register & email the certificate");
      setActive(null); load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt("Reason for rejecting this payment (shown to the studio):", "");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/protection-plans/${active.id}/reject`, { reason });
      toast.success("Payment rejected — studio notified to resubmit");
      setActive(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-brand-line shadow-none">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead><TableHead>Studio</TableHead>
              <TableHead>Vehicle</TableHead><TableHead>Area</TableHead>
              <TableHead>Plan amount</TableHead><TableHead>Proof / STR</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="p-0">
                <EmptyState icon={Wallet} title="No payments awaiting confirmation"
                  hint="Protection Plan opt-ins from job owners land here for you to confirm payment."
                  testid="protection-queue-empty" />
              </TableCell></TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`queue-row-${p.id}`}>
                <TableCell className="font-medium text-brand-ink">{p.customer_name}</TableCell>
                <TableCell className="text-slate-500">{p.studio_name}</TableCell>
                <TableCell>{p.vehicle_model} {p.registration_no}</TableCell>
                <TableCell>{p.area_sqft_covered} sq.ft.</TableCell>
                <TableCell>{p.payment_amount ? money(p.payment_amount) : "—"}</TableCell>
                <TableCell className="text-xs">
                  {p.str_number && <div data-testid={`queue-str-${p.id}`}>STR: {p.str_number}</div>}
                  {p.proof_path
                    ? <button className="text-brand underline" data-testid={`queue-proof-${p.id}`}
                        onClick={() => viewProof(p.proof_path)}>View proof</button>
                    : (!p.str_number && <span className="text-slate-400">none</span>)}
                </TableCell>
                <TableCell>
                  <Button size="sm" data-testid={`confirm-open-${p.id}`} onClick={() => open(p)}>
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="confirm-payment-dialog">
          <DialogHeader><DialogTitle>Review Protection Plan payment</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {active.customer_name} · {active.studio_name} · {active.area_sqft_covered} sq.ft.
                Confirm the payment. CRM will then register and email the certificate.
              </p>
              <div className="rounded-md border border-brand-line bg-slate-50 p-3 text-sm">
                <div><span className="text-slate-500">STR / reference:</span> {active.str_number || "—"}</div>
                <div className="mt-1">
                  <span className="text-slate-500">Proof of payment:</span>{" "}
                  {active.proof_path
                    ? <button className="text-brand underline" data-testid="dialog-view-proof"
                        onClick={() => viewProof(active.proof_path)}>{active.proof_filename || "View file"}</button>
                    : "—"}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Amount received (optional)</Label>
                  <Input className="mt-1" inputMode="decimal" data-testid="confirm-amount"
                    value={form.amount_received} onChange={(e) => setForm((f) => ({ ...f, amount_received: e.target.value }))} /></div>
                <div><Label className="text-xs">Date received</Label>
                  <Input className="mt-1" type="date" value={form.date_received}
                    onChange={(e) => setForm((f) => ({ ...f, date_received: e.target.value }))} /></div>
                <div><Label className="text-xs">Payment mode</Label>
                  <Input className="mt-1" data-testid="confirm-mode" value={form.payment_mode}
                    onChange={(e) => setForm((f) => ({ ...f, payment_mode: e.target.value }))} /></div>
                <div><Label className="text-xs">Reference</Label>
                  <Input className="mt-1" value={form.payment_reference}
                    onChange={(e) => setForm((f) => ({ ...f, payment_reference: e.target.value }))} /></div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="text-rose-600" data-testid="reject-payment-submit" disabled={busy} onClick={reject}>
              Reject
            </Button>
            <Button data-testid="confirm-payment-submit" disabled={busy} onClick={confirm}>
              Confirm payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const RegisterTab = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [active, setActive] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [edit, setEdit] = useState({});
  const load = useCallback(() => {
    api.get("/protection-plans").then(({ data }) =>
      setRows((data || []).filter((p) =>
        ["pending_payment_confirmation", "payment_confirmed"].includes(p.status)))
    ).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRegister = (p) => {
    setActive(p);
    setStartDate((p.invoice_date || p.payment_confirmed_date || new Date().toISOString().slice(0, 10)).slice(0, 10));
    setEdit({
      customer_name: p.customer_name || "",
      contact: p.contact || "",
      email: p.email || "",
      mailing_address: p.mailing_address || "",
      vehicle_model: p.vehicle_model || "",
      registration_no: p.registration_no || "",
      chassis_no: p.chassis_no || "",
      serial_number: p.serial_number || "",
      area_sqft_covered: p.area_sqft_covered ? String(p.area_sqft_covered) : "",
      body_parts_covered: p.body_parts_covered || "Full Body",
    });
  };

  const register = async () => {
    if (!active) return;
    if (!(Number(edit.area_sqft_covered) > 0)) return toast.error("Enter the area (sq. ft.) covered");
    setBusy(active.id);
    try {
      const { data } = await api.post(`/protection-plans/${active.id}/register`, {
        start_date: startDate || undefined,
        customer_name: edit.customer_name,
        contact: edit.contact,
        email: edit.email,
        mailing_address: edit.mailing_address,
        vehicle_model: edit.vehicle_model,
        registration_no: edit.registration_no,
        chassis_no: edit.chassis_no,
        serial_number: edit.serial_number,
        area_sqft_covered: Number(edit.area_sqft_covered),
        body_parts_covered: edit.body_parts_covered || "Full Body",
      });
      toast.success(`Registered · ${data.certificate_number} · review it, then click "Send to Customer"`);
      setActive(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };
  const setF = (k) => (e) => setEdit((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Card className="border-brand-line shadow-none">
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead><TableHead>Studio</TableHead>
              <TableHead>Vehicle</TableHead><TableHead>Area</TableHead>
              <TableHead>Status</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="p-0">
                <EmptyState icon={ShieldCheck} title="Nothing to register"
                  hint="Plans appear here once opted-in. You can register the certificate after the Accountant confirms payment."
                  testid="protection-register-empty" />
              </TableCell></TableRow>
            )}
            {rows.map((p) => {
              const ready = p.status === "payment_confirmed";
              return (
                <TableRow key={p.id} data-testid={`register-row-${p.id}`}>
                  <TableCell className="font-medium text-brand-ink">{p.customer_name}</TableCell>
                  <TableCell className="text-slate-500">{p.studio_name}</TableCell>
                  <TableCell>{p.vehicle_model} {p.registration_no}</TableCell>
                  <TableCell>{p.area_sqft_covered} sq.ft.</TableCell>
                  <TableCell><StatusPill s={p.status} /></TableCell>
                  <TableCell>
                    <Button size="sm" disabled={!ready || busy === p.id}
                      data-testid={`register-plan-${p.id}`} onClick={() => openRegister(p)}>
                      {ready ? "Register" : "Awaiting Accountant confirmation"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="protection-register-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Register Protection Plan — review details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {active?.customer_name} · {active?.studio_name}
            </p>
            <p className="text-[11px] text-slate-400">
              Pre-filled from the job — review and correct anything before issuing the certificate.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Customer name</Label>
                <Input className="mt-1" data-testid="protection-register-name"
                  value={edit.customer_name} onChange={setF("customer_name")} />
              </div>
              <div>
                <Label className="text-xs">Contact no.</Label>
                <Input className="mt-1" data-testid="protection-register-contact"
                  value={edit.contact} onChange={setF("contact")} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input className="mt-1" type="email" data-testid="protection-register-email"
                  placeholder="customer@email.com" value={edit.email} onChange={setF("email")} />
              </div>
              <div>
                <Label className="text-xs">Mailing address</Label>
                <Input className="mt-1" data-testid="protection-register-address"
                  value={edit.mailing_address} onChange={setF("mailing_address")} />
              </div>
              <div>
                <Label className="text-xs">Vehicle model</Label>
                <Input className="mt-1" data-testid="protection-register-vehicle"
                  value={edit.vehicle_model} onChange={setF("vehicle_model")} />
              </div>
              <div>
                <Label className="text-xs">Registration no.</Label>
                <Input className="mt-1" data-testid="protection-register-reg"
                  value={edit.registration_no} onChange={setF("registration_no")} />
              </div>
              <div>
                <Label className="text-xs">Chassis / VIN</Label>
                <Input className="mt-1" data-testid="protection-register-chassis"
                  value={edit.chassis_no} onChange={setF("chassis_no")} />
              </div>
              <div>
                <Label className="text-xs">Serial no.</Label>
                <Input className="mt-1" data-testid="protection-register-serial"
                  value={edit.serial_number} onChange={setF("serial_number")} />
              </div>
              <div>
                <Label className="text-xs">Area covered (sq. ft.) *</Label>
                <Input className="mt-1" inputMode="decimal" data-testid="protection-register-area"
                  value={edit.area_sqft_covered} onChange={setF("area_sqft_covered")} />
              </div>
              <div>
                <Label className="text-xs">Body parts covered</Label>
                <Input className="mt-1" data-testid="protection-register-parts"
                  value={edit.body_parts_covered} onChange={setF("body_parts_covered")} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Plan start date (from the studio's invoice date)</Label>
              <Input type="date" className="mt-1" data-testid="protection-register-start-date"
                value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <p className="mt-1 text-[11px] text-slate-400">
                Pre-filled from the invoice date; edit if needed. The 2-year cover runs from this date.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button data-testid="protection-register-confirm" disabled={busy === active?.id || !startDate}
              onClick={register}>
              {busy === active?.id ? "Registering…" : "Register & issue certificate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ClaimTab = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ sqft_replaced: "", claim_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    try {
      const { data } = await api.get("/protection-plans/lookup", { params: { q: q.trim() } });
      setResults(data); setSel(null);
      if (data.length === 0) toast.info("No active plan found for that search");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submit = async () => {
    if (!(Number(form.sqft_replaced) > 0)) return toast.error("Enter sq. ft. replaced");
    setBusy(true);
    try {
      const { data } = await api.post(`/protection-plans/${sel.id}/claims`, {
        sqft_replaced: Number(form.sqft_replaced), claim_date: form.claim_date, notes: form.notes,
      });
      toast.success(`Claim logged · remaining balance ${data.remaining_sqft_balance} sq.ft.`);
      setForm({ sqft_replaced: "", claim_date: new Date().toISOString().slice(0, 10), notes: "" });
      const { data: refreshed } = await api.get("/protection-plans/lookup", { params: { q: q.trim() } });
      setResults(refreshed);
      setSel(refreshed.find((p) => p.id === sel.id) || null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-brand-line shadow-none">
        <CardContent className="flex flex-wrap items-end gap-2 p-4">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs">Certificate number or customer phone / name</Label>
            <Input className="mt-1" data-testid="claim-search-input" value={q}
              onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
          </div>
          <Button data-testid="claim-search-btn" onClick={search}>
            <Search className="mr-2 h-4 w-4" /> Search
          </Button>
        </CardContent>
      </Card>

      {results.map((p) => (
        <Card key={p.id} className={`border-brand-line shadow-none ${sel?.id === p.id ? "ring-2 ring-crimson-400" : ""}`}
          data-testid={`claim-plan-${p.id}`}>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-medium text-brand-ink">{p.certificate_number} · {p.customer_name}</p>
                <p className="text-xs text-slate-500">
                  {p.vehicle_model} · original studio {p.studio_name} · valid until {p.valid_until}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Remaining balance</p>
                <p className="font-display text-lg font-semibold text-brand-ink">{p.remaining_sqft_balance} sq.ft.</p>
              </div>
            </div>
            {sel?.id === p.id ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div><Label className="text-xs">Sq. ft. replaced *</Label>
                  <Input className="mt-1" inputMode="decimal" data-testid="claim-sqft"
                    value={form.sqft_replaced} onChange={(e) => setForm((f) => ({ ...f, sqft_replaced: e.target.value }))} /></div>
                <div><Label className="text-xs">Claim date</Label>
                  <Input className="mt-1" type="date" value={form.claim_date}
                    onChange={(e) => setForm((f) => ({ ...f, claim_date: e.target.value }))} /></div>
                <div><Label className="text-xs">Notes</Label>
                  <Input className="mt-1" value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
                <div className="sm:col-span-3">
                  <Button data-testid="claim-submit-btn" disabled={busy} onClick={submit}>
                    <ClipboardList className="mr-2 h-4 w-4" /> Log claim
                  </Button>
                </div>
              </div>
            ) : (
              <Button size="sm" variant="outline" data-testid={`claim-select-${p.id}`} onClick={() => setSel(p)}>
                Service a claim on this plan
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default function Protection() {
  const { user } = useAuth();
  const canConfirm = can(user, "protection:confirm");
  const canRegister = can(user, "protection:register");
  const canClaim = can(user, "protection:claim");
  const isAdmin = user.role === "network_admin";
  const canFreeEntry = isAdmin || user.role === "crm";
  const [studios, setStudios] = useState([]);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (canFreeEntry) api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
  }, [canFreeEntry]);
  const first = canConfirm ? "queue" : canRegister ? "register" : canClaim ? "claim" : "plans";
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHead title="Protection Plans" subtitle="2-Year accidental-damage add-on · certificates & network-wide claims">
        {canFreeEntry && (
          <FreeEntryButton kind="protection" studios={studios} onDone={() => setRefresh((r) => r + 1)} />
        )}
      </PageHead>
      <Tabs defaultValue={first}>
        <TabsList>
          {canConfirm && <TabsTrigger value="queue" data-testid="tab-queue">Payment queue</TabsTrigger>}
          {canRegister && <TabsTrigger value="register" data-testid="tab-register">To register</TabsTrigger>}
          <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
          {canClaim && <TabsTrigger value="claim" data-testid="tab-claim">Service a claim</TabsTrigger>}
        </TabsList>
        {canConfirm && <TabsContent value="queue" className="mt-4"><QueueTab /></TabsContent>}
        {canRegister && <TabsContent value="register" className="mt-4"><RegisterTab /></TabsContent>}
        <TabsContent value="plans" className="mt-4"><PlansTab key={refresh} /></TabsContent>
        {canClaim && <TabsContent value="claim" className="mt-4"><ClaimTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
